import React, { useState } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { AuthenticatedImage } from '../extensions/AuthenticatedImage';
import { Citation, CitationAttributes } from '../extensions/Citation';
import { Placeholder } from '../extensions/Placeholder';
import { PiiRedact } from '../extensions/PiiRedact';
import { Redaction, redactionPluginKey } from '../extensions/Redaction';
import { useDocumentViewer } from '../contexts/DocumentViewerContext';
import { useRedactionMode } from '../contexts/RedactionModeContext';
import { EditorToolbar } from './EditorToolbar';
import { PlaceholderNavigator } from './PlaceholderNavigator';
import { markdownToHtml } from '../utils/markdownToProseMirror';
import { useEditorTableOfContents } from '../hooks/useEditorTableOfContents';

interface ReportEditorProps {
  content: string; // ProseMirror JSON as string
  readOnly?: boolean;
  onUpdate?: (content: string) => void;
  reportId?: string;
  caseId?: string;
  onEditorReady?: (editor: Editor) => void;
  onSelectionChange?: (text: string | null) => void;
  /** Hide the TOC sidebar (section list provides navigation instead). */
  hideToc?: boolean;
  /** Compact mode — reduced padding, smaller toolbar for sidebar embedding. */
  compact?: boolean;
  /** Called when user picks an export format from the toolbar. */
  onExport?: (format: 'word' | 'pdf') => void;
}

export const ReportEditor: React.FC<ReportEditorProps> = ({
  content,
  readOnly = false,
  onUpdate,
  reportId,
  caseId,
  onEditorReady,
  onSelectionChange,
  hideToc = false,
  compact = false,
  onExport,
}) => {
  const { openDocument } = useDocumentViewer();
  const { isEnabled: redactionEnabled, structuredPii } = useRedactionMode();
  const [tocCollapsed, setTocCollapsed] = useState(false);

  // Parse content — handles JSON string, raw object, markdown, and empty/null
  let parsedContent;
  try {
    if (!content || (typeof content === 'string' && content.trim() === '')) {
      console.warn('Empty content provided to ReportEditor');
      parsedContent = {
        type: 'doc',
        content: [
          {
            type: 'paragraph',
            content: [{ type: 'text', text: 'No content available' }],
          },
        ],
      };
    } else if (typeof content === 'object') {
      // Already a parsed ProseMirror JSON object
      parsedContent = content;
    } else {
      // Try JSON first
      try {
        parsedContent = JSON.parse(content);
      } catch {
        // Not valid JSON — treat as markdown/plain text and convert to HTML
        const html = markdownToHtml(content);
        parsedContent = html;
      }
    }
  } catch (error) {
    console.error('Failed to parse report content:', error);
    console.error('Content received (type:', typeof content, '):', typeof content === 'string' ? content.substring(0, 200) : content);
    parsedContent = {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: [{ type: 'text', text: 'Error loading report content. The content may be corrupted.' }],
        },
      ],
    };
  }

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Table.configure({
        resizable: false,
        HTMLAttributes: {
          class: 'report-table',
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
      AuthenticatedImage.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'report-image',
        },
      }),
      Citation.configure({
        onCitationClick: (attrs: CitationAttributes) => {
          if (attrs.documentId === 'doc-placeholder' || !attrs.documentId) return;
          openDocument({
            documentId: attrs.documentId,
            documentName: 'Document',
            pageNumber: attrs.pageNumber,
          });
        },
      }),
      Placeholder,
      PiiRedact,
      Redaction.configure({
        isEnabled: redactionEnabled,
        structuredPii,
      }),
    ],
    content: parsedContent,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      if (onUpdate) {
        const json = editor.getJSON();
        onUpdate(JSON.stringify(json));
      }
    },
    onSelectionUpdate: ({ editor }) => {
      if (onSelectionChange) {
        const { from, to } = editor.state.selection;
        if (to > from) {
          onSelectionChange(editor.state.doc.textBetween(from, to, ' '));
        } else {
          onSelectionChange(null);
        }
      }
    },
  });

  // Sync redaction state into the ProseMirror plugin via transaction metadata
  React.useEffect(() => {
    if (editor && !editor.isDestroyed) {
      const tr = editor.state.tr;
      tr.setMeta(redactionPluginKey, { isEnabled: redactionEnabled, structuredPii });
      editor.view.dispatch(tr);
    }
  }, [editor, redactionEnabled, structuredPii]);

  // Update editor content when content prop changes
  React.useEffect(() => {
    if (editor && content && !editor.isDestroyed) {
      const currentContent = JSON.stringify(editor.getJSON());
      if (currentContent !== content) {
        try {
          const newContent = JSON.parse(content);
          editor.commands.setContent(newContent, { emitUpdate: false });
        } catch (error) {
          console.error('Failed to update editor content:', error);
        }
      }
    }
  }, [editor, content]);

  // Notify parent when editor is ready
  React.useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  // Handle citation clicks
  React.useEffect(() => {
    if (!editor) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const citationElement = target.closest('[data-citation]');

      if (citationElement) {
        const documentId = citationElement.getAttribute('data-document-id');
        const pageNumber = citationElement.getAttribute('data-page-number');

        if (documentId && documentId !== 'doc-placeholder') {
          openDocument({
            documentId,
            documentName: 'Document',
            pageNumber: pageNumber ? parseInt(pageNumber, 10) : undefined,
          });
        }
      }
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener('click', handleClick);

    return () => {
      editorElement.removeEventListener('click', handleClick);
    };
  }, [editor]);

  const { entries: tocEntries, activeId, scrollToHeading } = useEditorTableOfContents(editor);

  if (!editor) {
    return <div className="text-medical-600">Loading editor...</div>;
  }

  const handleRevisionComplete = (revisedContent: string) => {
    try {
      const parsed = JSON.parse(revisedContent);
      editor.commands.setContent(parsed, { emitUpdate: false });
      if (onUpdate) {
        onUpdate(revisedContent);
      }
    } catch {
      // Streaming returns plain text/markdown — convert via marked → HTML
      const html = markdownToHtml(revisedContent);
      editor.commands.setContent(html, { emitUpdate: false });
      if (onUpdate) {
        onUpdate(JSON.stringify(editor.getJSON()));
      }
    }
  };

  const showTocSidebar = !readOnly && !hideToc && tocEntries.length > 0;

  return (
    <div className="relative w-full">
      {/* Toolbar - only show in edit mode */}
      {!readOnly && (
        <EditorToolbar
          editor={editor}
          compact={compact}
          onExport={onExport}
        />
      )}

      {/* Placeholder navigator - shows when placeholders exist */}
      {!readOnly && editor && <PlaceholderNavigator editor={editor} />}

      {/* Editor with TOC sidebar */}
      <div className="flex overflow-hidden">
        {/* TOC Sidebar */}
        {showTocSidebar && (
          <div
            className={`flex-shrink-0 border-r border-gray-200/80 dark:border-medical-600/60 bg-gray-50/50 dark:bg-medical-800/30 transition-all duration-200 ${
              tocCollapsed ? 'w-10' : 'w-52'
            }`}
          >
            <div className="sticky top-10 overflow-hidden">
              {/* Header with collapse toggle */}
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200/60 dark:border-medical-600/40">
                {!tocCollapsed && (
                  <p className="text-[10px] font-semibold text-medical-400 dark:text-medical-500 uppercase tracking-widest">
                    Contents
                  </p>
                )}
                <button
                  onClick={() => setTocCollapsed(!tocCollapsed)}
                  className="p-1 rounded-md text-medical-400 dark:text-medical-500 hover:text-medical-600 dark:hover:text-medical-300 hover:bg-medical-100 dark:hover:bg-medical-600/50 transition-colors"
                  title={tocCollapsed ? 'Expand contents' : 'Collapse contents'}
                >
                  <svg
                    className={`h-3.5 w-3.5 transition-transform duration-200 ${tocCollapsed ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              </div>

              {/* Entries */}
              {!tocCollapsed && (
                <nav className="py-2 max-h-[calc(100vh-12rem)] overflow-y-auto">
                  {tocEntries.map((entry) => (
                    <button
                      key={entry.id}
                      onClick={() => scrollToHeading(entry)}
                      className={`group w-full text-left py-1 transition-colors relative block
                        ${activeId === entry.id
                          ? 'text-primary-700 dark:text-primary-400 bg-primary-50/60 dark:bg-primary-900/15'
                          : 'text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-200 hover:bg-medical-100/50 dark:hover:bg-medical-600/30'
                        }
                        ${entry.level === 1 ? 'text-xs font-semibold pl-3 pr-2' : ''}
                        ${entry.level === 2 ? 'text-[11px] font-medium pl-5 pr-2' : ''}
                        ${entry.level === 3 ? 'text-[10px] pl-7 pr-2' : ''}
                      `}
                      title={entry.text}
                    >
                      {/* Active indicator bar */}
                      {activeId === entry.id && (
                        <span className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary-500 dark:bg-primary-400 rounded-r" />
                      )}
                      <span className="block truncate">{entry.text}</span>
                    </button>
                  ))}
                </nav>
              )}

              {/* Collapsed state: vertical "CONTENTS" label */}
              {tocCollapsed && (
                <button
                  onClick={() => setTocCollapsed(false)}
                  className="w-full py-4 flex justify-center"
                  title="Expand contents"
                >
                  <span className="text-[9px] font-semibold text-medical-400 dark:text-medical-500 uppercase tracking-widest"
                    style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                  >
                    Contents
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Editor content */}
        <div className="relative flex-1 min-w-0 overflow-hidden bg-white dark:bg-gray-100 rounded-b-lg">
          <EditorContent
            editor={editor}
            className={readOnly ? 'cursor-default' : ''}
          />


        </div>
      </div>

      {/* Compact + Citation Styles */}
      <style>{`${compact ? `
        .ProseMirror {
          min-height: 200px !important;
          padding: 1rem !important;
          overflow-wrap: break-word !important;
          word-break: break-word !important;
        }
        .ProseMirror h1 { font-size: 1.25rem !important; margin-bottom: 0.75rem !important; }
        .ProseMirror h2 { font-size: 1.1rem !important; margin-top: 1.5rem !important; margin-bottom: 0.5rem !important; }
        .ProseMirror h3 { font-size: 1rem !important; margin-top: 1.25rem !important; }
        .ProseMirror p { font-size: 0.875rem !important; margin-bottom: 0.75rem !important; line-height: 1.65 !important; }
        .ProseMirror ul, .ProseMirror ol { font-size: 0.875rem !important; padding-left: 1.25rem !important; }
        .ProseMirror li { font-size: 0.875rem !important; margin-bottom: 0.375rem !important; }
      ` : ''}
        .citation-mark {
          display: inline-flex;
          align-items: center;
          padding: 0.125rem 0.375rem;
          margin: 0 0.125rem;
          border-radius: 0.25rem;
          background-color: rgb(219 234 254); /* cyan-100 */
          color: rgb(29 78 216); /* cyan-700 */
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
          vertical-align: super;
          font-size: 0.75rem;
          line-height: 1;
        }

        .citation-mark:hover {
          background-color: rgb(191 219 254); /* cyan-200 */
        }

        .citation-mark::before {
          content: '[';
        }

        .citation-mark::after {
          content: ']';
        }

        /* Placeholder pill styles */
        .placeholder-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.125rem 0.5rem 0.125rem 0.25rem;
          margin: 0 0.125rem;
          border-radius: 9999px;
          background-color: rgb(254 243 199); /* amber-100 */
          border: 1px solid rgb(252 211 77); /* amber-300 */
          cursor: pointer;
          transition: all 0.15s ease;
          vertical-align: baseline;
          line-height: 1.5;
          user-select: none;
        }

        .placeholder-pill:hover {
          background-color: rgb(253 230 138); /* amber-200 */
          border-color: rgb(245 158 11); /* amber-500 */
          box-shadow: 0 0 0 2px rgb(245 158 11 / 0.15);
        }

        .ProseMirror-selectednode .placeholder-pill,
        .placeholder-pill.ProseMirror-selectednode {
          box-shadow: 0 0 0 2px rgb(245 158 11 / 0.4);
          border-color: rgb(245 158 11);
        }

        .placeholder-pill-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1rem;
          height: 1rem;
          border-radius: 50%;
          background-color: rgb(245 158 11); /* amber-500 */
          color: white;
          font-size: 0.625rem;
          font-weight: 700;
          flex-shrink: 0;
        }

        .placeholder-pill-text {
          font-size: 0.8125rem;
          font-style: italic;
          color: rgb(146 64 14); /* amber-800 */
          white-space: nowrap;
        }

        .placeholder-editing-wrapper {
          display: inline-flex;
          align-items: center;
        }

        .placeholder-input {
          display: inline-block;
          padding: 0.125rem 0.5rem;
          margin: 0 0.125rem;
          border-radius: 9999px;
          background-color: rgb(255 251 235); /* amber-50 */
          border: 2px solid rgb(245 158 11); /* amber-500 */
          font-size: 0.875rem;
          font-family: inherit;
          line-height: 1.5;
          outline: none;
          box-shadow: 0 0 0 2px rgb(245 158 11 / 0.2);
        }

        .placeholder-input::placeholder {
          color: rgb(180 83 9); /* amber-700 */
          font-style: italic;
          opacity: 0.6;
        }

        /* Placeholder navigator bar */
        .placeholder-navigator {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.375rem 0.75rem;
          background-color: rgb(254 243 199); /* amber-100 */
          border-bottom: 1px solid rgb(252 211 77); /* amber-300 */
          font-size: 0.8125rem;
          color: rgb(146 64 14); /* amber-800 */
        }

        .placeholder-navigator-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.125rem;
          height: 1.125rem;
          border-radius: 50%;
          background-color: rgb(245 158 11); /* amber-500 */
          color: white;
          font-size: 0.625rem;
          font-weight: 700;
          flex-shrink: 0;
        }

        .placeholder-navigator-label {
          font-weight: 500;
          flex: 1;
        }

        .placeholder-navigator-controls {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .placeholder-navigator-counter {
          font-size: 0.75rem;
          font-weight: 600;
          color: rgb(180 83 9); /* amber-700 */
          margin-right: 0.25rem;
        }

        .placeholder-navigator-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 0.25rem;
          border: none;
          background-color: transparent;
          color: rgb(146 64 14); /* amber-800 */
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .placeholder-navigator-btn:hover {
          background-color: rgb(253 230 138); /* amber-200 */
          color: rgb(120 53 15); /* amber-900 */
        }

        /* TipTap editor styles */
        .ProseMirror {
          outline: none;
          min-height: 400px;
          padding: 3rem 2rem;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
          max-width: 100%;
          overflow-x: hidden;
          word-break: break-word;
        }

        .ProseMirror h1 {
          font-size: 2.25rem;
          font-weight: 700;
          margin-top: 0;
          margin-bottom: 1.5rem;
          color: rgb(17 24 39); /* gray-900 */
          line-height: 1.2;
          letter-spacing: -0.025em;
        }

        .ProseMirror h2 {
          font-size: 1.875rem;
          font-weight: 700;
          margin-top: 3rem;
          margin-bottom: 1.25rem;
          color: rgb(17 24 39);
          line-height: 1.25;
          letter-spacing: -0.025em;
          border-bottom: 2px solid rgb(229 231 235);
          padding-bottom: 0.5rem;
        }

        .ProseMirror h2:first-child {
          margin-top: 0;
        }

        .ProseMirror h3 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-top: 2.5rem;
          margin-bottom: 1rem;
          color: rgb(31 41 55); /* gray-800 */
          line-height: 1.3;
        }

        .ProseMirror h4 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 2rem;
          margin-bottom: 0.875rem;
          color: rgb(31 41 55);
          line-height: 1.4;
        }

        .ProseMirror p {
          margin-bottom: 1.25rem;
          line-height: 1.8;
          color: rgb(55 65 81); /* gray-700 */
          font-size: 1.0625rem;
        }

        .ProseMirror p:last-child {
          margin-bottom: 0;
        }

        .ProseMirror ul {
          list-style-type: disc;
          margin-bottom: 1.25rem;
          padding-left: 2rem;
          line-height: 1.8;
        }

        .ProseMirror ol {
          list-style-type: decimal;
          margin-bottom: 1.25rem;
          padding-left: 2rem;
          line-height: 1.8;
        }

        .ProseMirror li {
          margin-bottom: 0.625rem;
          color: rgb(55 65 81);
        }

        .ProseMirror li:last-child {
          margin-bottom: 0;
        }

        .ProseMirror strong {
          font-weight: 600;
          color: rgb(17 24 39);
        }

        .ProseMirror em {
          font-style: italic;
        }

        .ProseMirror u {
          text-decoration: underline;
        }

        .ProseMirror s {
          text-decoration: line-through;
        }

        /* Highlight styles */
        .ProseMirror mark {
          padding: 0.125rem 0.25rem;
          border-radius: 0.125rem;
        }

        /* Text alignment */
        .ProseMirror [style*="text-align: left"] {
          text-align: left;
        }

        .ProseMirror [style*="text-align: center"] {
          text-align: center;
        }

        .ProseMirror [style*="text-align: right"] {
          text-align: right;
        }

        .ProseMirror [style*="text-align: justify"] {
          text-align: justify;
        }

        .ProseMirror code {
          background-color: rgb(243 244 246); /* gray-100 */
          padding: 0.125rem 0.375rem;
          border-radius: 0.25rem;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.9375rem;
          color: rgb(220 38 38); /* red-600 */
        }

        .ProseMirror pre {
          background-color: rgb(31 41 55); /* gray-800 */
          color: rgb(243 244 246); /* gray-100 */
          padding: 1.25rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin-bottom: 1.25rem;
        }

        .ProseMirror pre code {
          background-color: transparent;
          padding: 0;
          color: inherit;
          font-size: 0.9375rem;
        }

        .ProseMirror blockquote {
          border-left: 4px solid rgb(59 130 246); /* cyan-500 */
          padding-left: 1.25rem;
          margin-left: 0;
          margin-bottom: 1.25rem;
          color: rgb(75 85 99); /* gray-600 */
          font-style: italic;
          background-color: rgb(249 250 251); /* gray-50 */
          padding: 1rem 1.25rem;
          border-radius: 0.25rem;
        }

        .ProseMirror hr {
          border: none;
          border-top: 2px solid rgb(229 231 235); /* gray-200 */
          margin: 3rem 0;
        }

        /* Table styles */
        .ProseMirror table {
          border-collapse: collapse;
          width: 100%;
          margin-bottom: 1.5rem;
          font-size: 0.9375rem;
        }

        .ProseMirror th {
          background-color: rgb(243 244 246); /* gray-100 */
          font-weight: 600;
          color: rgb(31 41 55); /* gray-800 */
          border: 1px solid rgb(229 231 235); /* gray-200 */
          padding: 0.625rem 0.875rem;
          text-align: left;
        }

        .ProseMirror td {
          border: 1px solid rgb(229 231 235);
          padding: 0.5rem 0.875rem;
          color: rgb(55 65 81); /* gray-700 */
        }

        .ProseMirror tr:nth-child(even) td {
          background-color: rgb(249 250 251); /* gray-50 */
        }

        .ProseMirror tr:hover td {
          background-color: rgb(243 244 246); /* gray-100 */
        }

        .ProseMirror img.report-image {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1rem 0;
          border: 1px solid rgb(229 231 235);
        }

        /* Redaction blur */
        .ProseMirror .redaction-blur {
          filter: blur(5px);
          background-color: rgb(254 243 199 / 0.5); /* amber-100/50 */
          border-radius: 2px;
          transition: all 0.2s ease;
          cursor: pointer;
          user-select: none;
        }

        .ProseMirror .redaction-blur:hover {
          filter: blur(0);
          background-color: rgb(254 243 199 / 0.3);
          user-select: auto;
        }

        /* Read-only mode */
        .ProseMirror[contenteditable="false"] {
          cursor: default;
        }

        /* Focus styles for editable mode */
        .ProseMirror:focus {
          outline: none;
        }

        /* Selection styles */
        .ProseMirror ::selection {
          background-color: rgb(191 219 254); /* cyan-200 */
        }

        /* Placeholder for empty editor */
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: rgb(156 163 175); /* gray-400 */
          pointer-events: none;
          height: 0;
        }
      `}</style>
    </div>
  );
};
